import { useEffect, useMemo, useState, useCallback } from 'react';
import { doc, getDoc, onSnapshot, collection, updateDoc, arrayUnion, arrayRemove, Timestamp, setDoc, deleteDoc, getDocs, query, where, writeBatch, deleteField } from 'firebase/firestore';
import { toast } from '../ui/toast';
import { PlanService } from '../services/billing/PlanService';

export default function useMembersManager({ db, planningId, currentUserId }) {
  const [ownerId, setOwnerId] = useState(null); // legacy single owner (creator)
  const [owners, setOwners] = useState([]); // multi-owner support
  const [participants, setParticipants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [atMemberCap, setAtMemberCap] = useState(false);
  const [rolesByUser, setRolesByUser] = useState(new Map());
  const [memberProfiles, setMemberProfiles] = useState(new Map()); // uid -> { displayName, contract, weeklyHoursMax }
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [eventRoleSuggestions, setEventRoleSuggestions] = useState([]);

  const isOwner = useMemo(() => {
    const arr = Array.isArray(owners) ? owners : [];
    return (currentUserId && (arr.includes(currentUserId) || ownerId === currentUserId)) || false;
  }, [owners, ownerId, currentUserId]);

  useEffect(() => {
    if (!planningId) return;
    const unsub = onSnapshot(doc(db, 'plannings', planningId), async (snap) => {
      if (!snap.exists()) return;
      const d = snap.data() || {};
      const owner = d.owner || null; // legacy creator
      setOwnerId(owner);
      const ownersArr = Array.isArray(d.owners) && d.owners.length ? Array.from(new Set(d.owners.filter(Boolean))) : (owner ? [owner] : []);
      setOwners(ownersArr);
      const uids = [...ownersArr, ...(Array.isArray(d.participants) ? d.participants : [])].filter(Boolean);
      const userDocs = await Promise.all(
        uids.map((uid) => getDoc(doc(db, 'users', uid)).catch(() => null))
      );
      const profiles = userDocs
        .filter((s) => s && s.exists())
        .map((s) => {
          const u = s.data() || {};
          // Merge roles and contract from sub-collection listener
          const base = { uid: s.id, displayName: u.displayName || u.email || s.id, email: u.email || null, roles: rolesByUser.get(s.id) || [] };
          const prof = memberProfiles.get(s.id) || {};
          return { ...base, contract: prof.contract || 'internal', weeklyHoursMax: prof.weeklyHoursMax != null ? prof.weeklyHoursMax : undefined };
        });
      // Include ghost members (from members subcollection) that don't have a users profile
      for (const [uid, prof] of memberProfiles.entries()) {
        if (!profiles.some((p) => p.uid === uid)) {
          profiles.push({
            uid,
            displayName: prof.displayName || uid,
            email: null,
            roles: rolesByUser.get(uid) || [],
            contract: prof.contract || 'internal',
            weeklyHoursMax: (prof.weeklyHoursMax != null ? prof.weeklyHoursMax : undefined),
          });
        }
      }
      setParticipants(profiles);
      // Compute plan member cap for owner and whether we're at capacity
      try {
        // Use the legacy primary owner for plan limits
        if (owner) {
          const plan = await PlanService.getCurrentPlan(owner);
          const { maxMembersPerCalendar } = PlanService.getLimits(plan);
          // profiles includes owner + participants
          setAtMemberCap(profiles.length >= maxMembersPerCalendar);
        } else {
          setAtMemberCap(false);
        }
      } catch {
        setAtMemberCap(false);
      }
    });
    return () => unsub();
  }, [db, planningId, rolesByUser, memberProfiles]);

  // Listen to member roles stored under plannings/{id}/members/{uid}
  useEffect(() => {
    if (!planningId) return;
    const membersCol = collection(db, 'plannings', planningId, 'members');
    const unsub = onSnapshot(membersCol, (snap) => {
      const roleMap = new Map();
      const profMap = new Map();
      for (const d of snap.docs) {
        const data = d.data() || {};
        const roles = Array.isArray(data.roles) ? data.roles.filter((r) => typeof r === 'string' && r.trim()).map((r) => r.trim()) : [];
        roleMap.set(d.id, roles);
        const dn = String(data.displayName || '').trim();
        const contract = (data.contract === 'external' || data.contract === 'internal') ? data.contract : 'internal';
        const weeklyHoursMax = Number(data.weeklyHoursMax);
        const prev = profMap.get(d.id) || {};
        profMap.set(d.id, { ...prev, ...(dn ? { displayName: dn } : {}), contract, ...(Number.isFinite(weeklyHoursMax) && weeklyHoursMax >= 0 ? { weeklyHoursMax } : {}) });
      }
      setRolesByUser(roleMap);
      setMemberProfiles(profMap);
    });
    return () => unsub();
  }, [db, planningId]);

  // Owner management
  const makeOwner = useCallback(async (uid) => {
    if (!planningId || !uid || !isOwner) return false;
    try {
      // Only allow promoting real users (not ghosts): must have a users/{uid} doc
      const profile = await getDoc(doc(db, 'users', uid));
      if (!profile.exists()) {
        toast.show({ title: 'Not allowed', message: 'Ghost members cannot be owners', color: 'red' });
        return false;
      }
      const planningRef = doc(db, 'plannings', planningId);
      // Ensure legacy creator remains an owner when introducing owners[] on older docs
      const ownersUpdate = ownerId ? { owners: arrayUnion(uid, ownerId) } : { owners: arrayUnion(uid) };
      await updateDoc(planningRef, { ...ownersUpdate, participants: arrayRemove(uid) });
      toast.show({ title: 'Owner added', message: 'Member promoted to owner', color: 'green' });
      return true;
    } catch (e) {
      toast.show({ title: 'Failed', message: String(e?.message || e), color: 'red' });
      return false;
    }
  }, [db, planningId, isOwner, ownerId]);

  const removeOwner = useCallback(async (uid) => {
    if (!planningId || !uid || !isOwner) return false;
    try {
      const ownersArr = Array.isArray(owners) ? owners : [];
      if (ownersArr.length <= 1) {
        toast.show({ title: 'Not allowed', message: 'At least one owner is required', color: 'yellow' });
        return false;
      }
      const planningRef = doc(db, 'plannings', planningId);
      // Demote to regular participant
      await updateDoc(planningRef, { owners: arrayRemove(uid), participants: arrayUnion(uid) });
      toast.show({ title: 'Owner removed', message: 'Member demoted from owner', color: 'green' });
      return true;
    } catch (e) {
      toast.show({ title: 'Failed', message: String(e?.message || e), color: 'red' });
      return false;
    }
  }, [db, planningId, isOwner, owners]);

  // Listen to roles used in events (rolesRequired on each event)
  useEffect(() => {
    if (!planningId) return;
    const eventsCol = collection(db, 'plannings', planningId, 'events');
    const unsub = onSnapshot(eventsCol, (snap) => {
      const set = new Set();
      for (const d of snap.docs) {
        const data = d.data() || {};
        const rr = Array.isArray(data.rolesRequired) ? data.rolesRequired : [];
        rr.forEach((r) => {
          const name = String(r?.role || '').trim();
          if (name) set.add(name);
        });
      }
      setEventRoleSuggestions(Array.from(set).sort((a, b) => a.localeCompare(b)));
    });
    return () => unsub();
  }, [db, planningId]);

  // Combine member roles + roles found in events into suggestions
  useEffect(() => {
    const all = new Set(eventRoleSuggestions);
    for (const roles of rolesByUser.values()) {
      (roles || []).forEach((r) => all.add(r));
    }
    setRoleSuggestions(Array.from(all).sort((a, b) => a.localeCompare(b)));
  }, [rolesByUser, eventRoleSuggestions]);

  useEffect(() => {
    if (!planningId || !isOwner) return;
    const unsub = onSnapshot(collection(db, 'plannings', planningId, 'joinRequests'), (snap) => {
      const rows = snap.docs.map((d) => ({ uid: d.id, ...(d.data() || {}) }));
      setRequests(rows);
    });
    return () => unsub();
  }, [db, planningId, isOwner]);

  const approveRequest = useCallback(async (uid) => {
    if (!isOwner || !planningId) return;
    try {
      const plan = await PlanService.getCurrentPlan(ownerId || currentUserId);
      const { maxMembersPerCalendar } = PlanService.getLimits(plan);
      // participants includes owner profile as built from planning doc subscription
      const total = participants.length;
      if (total >= maxMembersPerCalendar) {
        toast.show({ title: 'Plan limit', message: 'This calendar is at capacity for the owner’s plan', color: 'red' });
        return;
      }
    } catch {}
    const planningRef = doc(db, 'plannings', planningId);
    await updateDoc(planningRef, { participants: arrayUnion(uid) });
    // Ensure a member doc exists with a default role
    try {
      const memberRef = doc(db, 'plannings', planningId, 'members', uid);
      await setDoc(memberRef, { roles: ['Member'], joinedAt: Timestamp.fromDate(new Date()), joinedBy: currentUserId }, { merge: true });
    } catch (_) {}
    const reqRef = doc(db, 'plannings', planningId, 'joinRequests', uid);
    await updateDoc(reqRef, { status: 'approved', decidedAt: Timestamp.fromDate(new Date()), decidedBy: currentUserId });
  }, [db, planningId, currentUserId, isOwner, ownerId, participants.length]);

  const rejectRequest = useCallback(async (uid) => {
    if (!isOwner || !planningId) return;
    const reqRef = doc(db, 'plannings', planningId, 'joinRequests', uid);
    await updateDoc(reqRef, { status: 'rejected', decidedAt: Timestamp.fromDate(new Date()), decidedBy: currentUserId });
  }, [db, planningId, currentUserId, isOwner]);

  const removeParticipant = useCallback(async (uid) => {
    if (!isOwner || !planningId) return;
    const planningRef = doc(db, 'plannings', planningId);
    const memberRef = doc(db, 'plannings', planningId, 'members', uid);
    // Ensure this uid is considered a known member by rules, then delete, then unlist
    try {
      await updateDoc(planningRef, { participants: arrayUnion(uid) });
    } catch (_) {}
    try {
      await deleteDoc(memberRef);
    } catch (e) {
      toast.show({ title: 'Remove failed', message: String(e?.message || e), color: 'red' });
      return;
    }
    try {
      await updateDoc(planningRef, { participants: arrayRemove(uid) });
    } catch (_) {}
    // Also mark any existing join request as rejected so the user can re-request later
    try {
      const reqRef = doc(db, 'plannings', planningId, 'joinRequests', uid);
      await updateDoc(reqRef, { status: 'rejected', decidedAt: Timestamp.fromDate(new Date()), decidedBy: currentUserId });
    } catch (_) {}

    // Clear this user's assignments from all events in this planning
    try {
      const eventsCol = collection(db, 'plannings', planningId, 'events');
      const q = query(eventsCol, where('assignedTo', 'array-contains', uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const batch = writeBatch(db);
        for (const d of snap.docs) {
          const data = d.data() || {};
          let newAssignedTo = [];
          let updates = {};
          if (Array.isArray(data.assignments)) {
            const filtered = data.assignments.filter((a) => a && a.uid !== uid);
            newAssignedTo = Array.from(new Set(filtered.map((a) => a.uid)));
            updates.assignments = filtered;
            updates.assignedTo = newAssignedTo;
          } else if (data.assignments && typeof data.assignments === 'object') {
            const obj = {};
            for (const [role, arr] of Object.entries(data.assignments)) {
              const list = Array.isArray(arr) ? arr.filter((u) => u !== uid) : [];
              obj[role] = list;
            }
            newAssignedTo = Array.from(new Set(Object.values(obj).flat().filter(Boolean)));
            updates.assignments = obj;
            updates.assignedTo = newAssignedTo;
          } else {
            // No structured assignments; just clear assignedTo
            updates.assignedTo = arrayRemove(uid);
          }
          batch.update(d.ref, updates);
        }
        await batch.commit();
      }
    } catch (e) {
      // Non-fatal: assignments cleanup failed
      console.warn('Failed to clear assignments for removed user', e);
    }
  }, [db, planningId, isOwner, currentUserId]);

  const addRole = useCallback(async (uid, role) => {
    if (!planningId || !uid || !role) return;
    const r = String(role).trim();
    if (!r) return;
    const memberRef = doc(db, 'plannings', planningId, 'members', uid);
    await setDoc(memberRef, { roles: arrayUnion(r) }, { merge: true });
  }, [db, planningId]);

  const removeRole = useCallback(async (uid, role) => {
    if (!planningId || !uid || !role) return;
    const r = String(role).trim();
    if (!r) return;
    const memberRef = doc(db, 'plannings', planningId, 'members', uid);
    await setDoc(memberRef, { roles: arrayRemove(r) }, { merge: true });
  }, [db, planningId]);

  const addGhostMember = useCallback(async ({ displayName, roles = [], contract = 'internal' }) => {
    if (!planningId) return null;
    // Enforce owner's plan limits for total members (owner + participants)
    try {
      const plan = await PlanService.getCurrentPlan(ownerId || currentUserId);
      const { maxMembersPerCalendar } = PlanService.getLimits(plan);
      const total = participants.length; // includes owner, participants, and ghosts merged above
      if (total >= maxMembersPerCalendar) {
        toast.show({ title: 'Plan limit', message: 'This calendar is at capacity for your plan', color: 'red' });
        return null;
      }
    } catch (_) {
      // If plan lookup fails, allow add to proceed rather than failing silently
    }
    const name = String(displayName || '').trim();
    if (!name) return null;
    const membersCol = collection(db, 'plannings', planningId, 'members');
    const { doc: mkDoc, setDoc: setDocFn } = await import('firebase/firestore');
    const tmpRef = mkDoc(membersCol);
    const newUid = tmpRef.id;
    const roleArr = Array.isArray(roles) ? roles : [];
    try {
      // 1) Add to planning participants first so rules consider this uid a known member
      const planningRef = doc(db, 'plannings', planningId);
      await updateDoc(planningRef, { participants: arrayUnion(newUid) });
      // 2) Create/update member doc
      await setDocFn(tmpRef, { displayName: name, roles: roleArr, contract: (contract === 'external' ? 'external' : 'internal'), joinedAt: Timestamp.fromDate(new Date()) }, { merge: true });
    } catch (e) {
      toast.show({ title: 'Add member failed', message: String(e?.message || e), color: 'red' });
      return null;
    }
    // Optimistic local reflect
    try {
      setMemberProfiles((prev) => {
        const m = new Map(prev);
        m.set(newUid, { displayName: name, contract: (contract === 'external' ? 'external' : 'internal') });
        return m;
      });
      setRolesByUser((prev) => {
        const m = new Map(prev);
        m.set(newUid, roleArr);
        return m;
      });
    } catch {}
    toast.show({ title: 'Member added', message: `${name} added to calendar`, color: 'green' });
    return newUid;
  }, [db, planningId, ownerId, currentUserId, participants.length]);

  const setContract = useCallback(async (uid, contract) => {
    if (!planningId || !uid) return;
    const val = (contract === 'external') ? 'external' : 'internal';
    const memberRef = doc(db, 'plannings', planningId, 'members', uid);
    await setDoc(memberRef, { contract: val }, { merge: true });
    // Optimistic local reflect
    setMemberProfiles((prev) => {
      const m = new Map(prev);
      const cur = m.get(uid) || {};
      m.set(uid, { ...cur, contract: val });
      return m;
    });
  }, [db, planningId]);

  const setWeeklyHoursMax = useCallback(async (uid, hours) => {
    if (!planningId || !uid) return false;
    const val = Number(hours);
    const memberRef = doc(db, 'plannings', planningId, 'members', uid);
    try {
      if (!Number.isFinite(val) || val <= 0) {
        // Unset the personalized cap: remove the field
        await setDoc(memberRef, { weeklyHoursMax: deleteField() }, { merge: true });
        setMemberProfiles((prev) => {
          const m = new Map(prev);
          const cur = m.get(uid) || {};
          const { weeklyHoursMax: _old, ...rest } = cur;
          m.set(uid, rest);
          return m;
        });
      } else {
        const n = Math.max(1, Math.min(168, Math.round(val)));
        await setDoc(memberRef, { weeklyHoursMax: n }, { merge: true });
        setMemberProfiles((prev) => {
          const m = new Map(prev);
          const cur = m.get(uid) || {};
          m.set(uid, { ...cur, weeklyHoursMax: n });
          return m;
        });
      }
      return true;
    } catch (e) {
      toast.show({ title: 'Save failed', message: 'Unable to update weekly hours', color: 'red' });
      return false;
    }
  }, [db, planningId]);

  return { ownerId, owners, isOwner, participants, requests, approveRequest, rejectRequest, removeParticipant, atMemberCap, roleSuggestions, addRole, removeRole, addGhostMember, setContract, setWeeklyHoursMax, makeOwner, removeOwner };
}
